<template>
  <div class="app">
    <div class="app-search">
      <div class="app-search-input">
        <el-input
          v-model="search.key"
          placeholder="搜索（核销人/订单号/备注）"
          clearable
          @clear="searchList"
          @keyup.enter="searchList"
        />
        <el-date-picker
          v-model="search.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
          style="margin-left: 12px"
          @change="searchList"
        />
      </div>
      <div class="app-search-btn">
        <el-button type="primary" @click="searchList">
          <el-icon><Search /></el-icon>查询
        </el-button>
      </div>
    </div>

    <!-- 统计汇总 -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-num">{{ stats.totalRecords }}</div>
        <div class="stat-label">核销总次数</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ stats.totalCount }}</div>
        <div class="stat-label">核销总份数</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ stats.shopCount }}</div>
        <div class="stat-label">涉及商户</div>
      </div>
    </div>

    <div class="app-table">
      <el-table
        :data="tableData.records"
        stripe
        v-loading="tableLoading"
        border
        style="width: 100%"
        row-key="id"
        max-height="calc(100vh - 340px)"
      >
        <el-table-column label="序号" type="index" width="60" align="center" />
        <el-table-column prop="heXiaoRenXm" label="核销人" width="120" />
        <el-table-column label="商户" min-width="150" show-overflow-tooltip>
          <template #default="scope">
            <span>{{ getShangHuMc(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="订单编号" width="180" show-overflow-tooltip>
          <template #default="scope">
            <span>{{ getDdBh(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="核销份数" width="90" align="center">
          <template #default="scope">
            <el-tag type="success">{{ scope.row.heXiaoSl }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="heXiaoLx" label="核销方式" width="110" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.heXiaoLx === '当面扫码'">{{ scope.row.heXiaoLx }}</el-tag>
            <el-tag v-else type="info">{{ scope.row.heXiaoLx || '当面扫码' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="heXiaoSj" label="核销时间" width="170">
          <template #default="scope">
            {{ formatTime(scope.row.heXiaoSj) }}
          </template>
        </el-table-column>
        <el-table-column prop="bz" label="备注" min-width="120" show-overflow-tooltip />
      </el-table>

      <el-pagination
        v-if="tableData.total > 0"
        style="margin-top: 20px; justify-content: flex-end"
        background
        layout="total, prev, pager, next, sizes"
        :current-page="search.pageIndex"
        :page-size="search.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="tableData.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { hxjlList } from '@/http/api'

const search = reactive({
  key: '',
  dateRange: [],
  pageIndex: 1,
  pageSize: 20,
})

const tableData = reactive({ records: [], total: 0 })
const tableLoading = ref(false)

const stats = computed(() => {
  const records = tableData.records
  const shopIds = new Set()
  let totalCount = 0
  records.forEach((r) => {
    totalCount += r.heXiaoSl || 0
    if (r.shangHuId) shopIds.add(r.shangHuId)
  })
  return {
    totalRecords: records.length,
    totalCount,
    shopCount: shopIds.size,
  }
})

function getParams() {
  return {
    Key: search.key || undefined,
    PageIndex: search.pageIndex,
    PageSize: search.pageSize,
  }
}

async function getList() {
  tableLoading.value = true
  try {
    const res = await hxjlList(getParams())
    tableData.records = res?.data?.records || res?.records || []
    tableData.total = res?.data?.total || res?.total || 0
  } catch {
    // handled by interceptor
  } finally {
    tableLoading.value = false
  }
}

function searchList() {
  search.pageIndex = 1
  getList()
}

function handleSizeChange(val) {
  search.pageSize = val
  search.pageIndex = 1
  getList()
}

function handleCurrentChange(val) {
  search.pageIndex = val
  getList()
}

function formatTime(time) {
  if (!time) return '--'
  return time.substring(0, 19).replace('T', ' ')
}

function getShangHuMc(row) {
  return row.shangHuMc || `商户#${row.shangHuId}` || '--'
}

function getDdBh(row) {
  return row.ddBh || `订单#${row.ddId}` || '--'
}

onMounted(() => {
  getList()
})
</script>

<style scoped>
.app {
  padding: 20px;
}
.app-search {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.app-search-input {
  display: flex;
  align-items: center;
  flex: 1;
}
.app-search-btn {
  margin-left: 16px;
}

/* 统计卡片 */
.stats-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}
.stat-card {
  flex: 1;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 12px;
  padding: 20px 24px;
  text-align: center;
  color: #fff;
}
.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}
.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}
.stat-num {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 4px;
}
.stat-label {
  font-size: 14px;
  opacity: 0.9;
}
</style>
